import {
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import {
  CaretLeftIcon,
  CaretRightIcon,
  ChartBarIcon,
  ChurchIcon,
  SquaresFourIcon,
  StackIcon,
  TreeStructureIcon,
  UserCircleIcon,
  UserGearIcon
} from "@phosphor-icons/react";
import SidebarPhoto from "assets/images/Photo.png";
import { IMAGE_URL } from "config";
import { useDenominationStore } from "features/denominations";
import { capitalize } from "lodash";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAclStore } from "stores/aclStore";
import { useSidebarStore } from "stores/sidebarStore";

export function DenominationDashboardSidebar() {
  const navigate = useNavigate();
  const selectedDenomination = useDenominationStore(
    state => state.selectedDenomination
  );
  const role = useAclStore(state => state.role);
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isOpen = useSidebarStore(state => state.isOpen);
  const setIsOpen = useSidebarStore(state => state.setIsOpen);

  const handleDrawerToggle = () => {
    setIsOpen(!isOpen);
  };

  const sidebarItems = [
    {
      name: t("denominations.sidebar.dashboard"),
      icon: <SquaresFourIcon size={24} />,
      path: `/denomination-dashboard/${selectedDenomination?.id}`
    },
    {
      name: t("denominations.sidebar.structures"),
      icon: <TreeStructureIcon size={24} />,
      path: `/denomination-dashboard/${selectedDenomination?.id}/structures`
    },
    {
      name: t("denominations.sidebar.churches"),
      icon: <ChurchIcon size={24} />,
      path: `/denomination-dashboard/${selectedDenomination?.id}/churches`
    },
    {
      name: t("denominations.sidebar.reports"),
      icon: <ChartBarIcon size={24} />,
      path: `/denomination-dashboard/${selectedDenomination?.id}/reports`
    },
    {
      name: t("denominations.sidebar.ministers"),
      icon: <UserGearIcon size={24} />,
      path: `/denomination-dashboard/${selectedDenomination?.id}/hr`
    },
    {
      name: t("Asset.Asset.Asset"),
      icon: <StackIcon size={24} />,
      path: `/denomination-dashboard/${selectedDenomination?.id}/assets`
    },
    {
      name: t("denominations.sidebar.systemAdmins"),
      icon: <UserCircleIcon size={24} />,
      path: `/denomination-dashboard/${selectedDenomination?.id}/users`,
      // Role management pages live under /roles, outside the /users tree.
      matchPaths: [`/denomination-dashboard/${selectedDenomination?.id}/roles`]
    }
  ];

  // Highlight a menu item when the current route matches its path or any related
  // path. Prefix matching keeps the parent active on nested routes; the
  // dashboard root matches exactly so it isn't always active.
  const dashboardRoot = `/denomination-dashboard/${selectedDenomination?.id}`;

  const isItemActive = item => {
    if (item.path === dashboardRoot) {
      return pathname === dashboardRoot;
    }

    const paths = [item.path, ...(item.matchPaths ?? [])];

    return paths.some(
      path => pathname === path || pathname.startsWith(`${path}/`)
    );
  };

  return (
    <Drawer
      variant="permanent"
      open={isOpen}
      sx={{
        width: isOpen ? 240 : 70,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: isOpen ? 240 : 70,
          boxSizing: "border-box",
          transition: theme =>
            theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen
            })
        }
      }}
    >
      <Box
        sx={{
          backgroundImage: `url(${SidebarPhoto})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          py: {
            xs: 4,
            md: 6
          },
          display: "flex",
          flexDirection: "column",
          color: "white",
          height: "100%",
          alignItems: {
            xs: "center",
            md: "inherit"
          }
        }}
      >
        <Stack alignItems="center" spacing={2} sx={{ mx: { xs: 6, md: 0 } }}>
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              color: "white",
              alignSelf: isOpen ? "flex-end" : "center"
            }}
          >
            {isOpen ? (
              <CaretLeftIcon size={24} color="#ffffff" />
            ) : (
              <CaretRightIcon size={24} color="#ffffff" />
            )}
          </IconButton>
          {isOpen && (
            <>
              <Stack direction="row" justifyContent="center">
                <Avatar
                  alt={capitalize(selectedDenomination?.name)}
                  src={`${IMAGE_URL}/${selectedDenomination?.image_url}`}
                  sx={{
                    width: 160,
                    height: 160,
                    border: "6px solid white"
                  }}
                />
              </Stack>
              <Typography variant="h6" textAlign="center" px={1}>
                {capitalize(selectedDenomination?.name)}
              </Typography>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "white",
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "white",
                    color: "primary.main"
                  }
                }}
                onClick={() =>
                  navigate(`/denominations/${selectedDenomination?.id}`)
                }
              >
                {t("denominations.sidebar.viewProfile")}
              </Button>
            </>
          )}
          {!isOpen && (
            <Avatar
              alt={capitalize(selectedDenomination?.name)}
              src={`${IMAGE_URL}/${selectedDenomination?.image_url}`}
              sx={{
                width: 50,
                height: 50,
                border: "3px solid white"
              }}
            />
          )}
        </Stack>
        <List sx={{ my: "auto", overflowY: "auto" }}>
          {sidebarItems.map(item => {
            if (item.role && item.role !== role) {
              return null;
            }

            const active = isItemActive(item);

            return (
              <ListItem key={item.name} disablePadding>
                <Tooltip title={isOpen ? "" : item.name} placement="right">
                  <ListItemButton
                    sx={{
                      pl: 3,
                      "&:hover": {
                        backgroundColor: "white",
                        color: "primary.main"
                      },
                      "&:hover > .MuiListItemIcon-root": {
                        color: "primary.main"
                      },
                      "&.active": {
                        backgroundColor: "white",
                        color: "primary.main",
                        ".MuiListItemIcon-root": {
                          color: "primary.main"
                        }
                      }
                    }}
                    component={Link}
                    to={item.path}
                    className={active ? "active" : undefined}
                    aria-current={active ? "page" : undefined}
                  >
                    <ListItemIcon sx={{ color: "white", minWidth: "34px" }}>
                      {item.icon}
                    </ListItemIcon>
                    {isOpen && <ListItemText primary={item.name} />}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
}
